// IMPEDIMENT TRACKING - Main UI Component
// Track and manage impediments/blockers.
// Phase 9: Impediment Tracking

import React, {useState, useEffect} from 'react'
import {Row, Col, Card, Button, Modal, Form, Badge, Tab, Tabs} from 'react-bootstrap'
import {format, parseISO} from 'date-fns'
import {toast} from 'react-toastify'
import Swal from 'sweetalert2'
import {useAuth} from '../../modules/auth'
import {
  getImpediments,
  createImpediment,
  updateImpediment,
  deleteImpediment,
  addImpedimentComment,
  Impediment,
  ImpedimentCreateInput,
  ImpedimentPriority,
  ImpedimentStatus,
} from '../../../api/ImpedimentAPI'

const ImpedimentTracking = () => {
  const {currentUser} = useAuth()
  const userId = currentUser?.id || ''

  // State
  const [impediments, setImpediments] = useState<Impediment[]>([])
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('open')
  const [showModal, setShowModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedImpediment, setSelectedImpediment] = useState<Impediment | null>(null)
  const [comment, setComment] = useState('')

  // Form state
  const [formData, setFormData] = useState<ImpedimentCreateInput>({
    userId,
    title: '',
    description: '',
    priority: ImpedimentPriority.MEDIUM,
    impactedArea: '',
    tags: [],
  })

  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({})
  const [summary, setSummary] = useState<Record<string, number>>({})

  useEffect(() => {
    loadImpediments()
  }, [userId])

  const loadImpediments = async () => {
    if (!userId) return
    setLoading(true)
    try {
      const data = await getImpediments({userId})
      setImpediments(data.impediments || [])
      setSummary(data.summary || {})
    } catch (error: any) {
      toast.error(error.message || 'Failed to load impediments')
    } finally {
      setLoading(false)
    }
  }

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {}

    if (!formData.title || formData.title.trim().length < 3) {
      errors.title = 'Title must be at least 3 characters'
    }

    if (!formData.description || formData.description.trim().length < 10) {
      errors.description = 'Description must be at least 10 characters'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    try {
      await createImpediment({
        ...formData,
        userId,
      })
      toast.success('Impediment logged successfully!')
      setShowModal(false)
      resetForm()
      loadImpediments()
    } catch (error: any) {
      toast.error(error.message || 'Failed to create impediment')
    }
  }

  const handleUpdateStatus = async (id: string, status: ImpedimentStatus) => {
    try {
      await updateImpediment(id, {userId, status})
      toast.success(`Status updated to ${status}`)
      loadImpediments()
      if (selectedImpediment?.id === id) {
        setShowDetailModal(false)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update status')
    }
  }

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: 'Delete Impediment?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it',
    })

    if (result.isConfirmed) {
      try {
        await deleteImpediment(id, userId)
        toast.success('Impediment deleted successfully')
        loadImpediments()
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete impediment')
      }
    }
  }

  const handleAddComment = async () => {
    if (!selectedImpediment || !comment.trim()) {
      toast.error('Please enter a comment')
      return
    }

    try {
      await addImpedimentComment(selectedImpediment.id, userId, comment)
      toast.success('Comment added')
      setComment('')
      loadImpediments()
    } catch (error: any) {
      toast.error(error.message || 'Failed to add comment')
    }
  }

  const resetForm = () => {
    setFormData({
      userId,
      title: '',
      description: '',
      priority: ImpedimentPriority.MEDIUM,
      impactedArea: '',
      tags: [],
    })
    setFormErrors({})
  }

  const openModal = () => {
    resetForm()
    setShowModal(true)
  }

  const openDetailModal = (impediment: Impediment) => {
    setSelectedImpediment(impediment)
    setComment('')
    setShowDetailModal(true)
  }

  const getStatusColor = (status: ImpedimentStatus) => {
    switch (status) {
      case 'OPEN':
        return 'danger'
      case 'IN_PROGRESS':
        return 'warning'
      case 'RESOLVED':
        return 'success'
      case 'CLOSED':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  const getPriorityColor = (priority: ImpedimentPriority) => {
    switch (priority) {
      case 'CRITICAL':
        return 'danger'
      case 'HIGH':
        return 'warning'
      case 'MEDIUM':
        return 'info'
      case 'LOW':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  const filteredImpediments = () => {
    switch (activeTab) {
      case 'open':
        return impediments.filter((i) => i.status === 'OPEN')
      case 'in-progress':
        return impediments.filter((i) => i.status === 'IN_PROGRESS')
      case 'resolved':
        return impediments.filter((i) => i.status === 'RESOLVED' || i.status === 'CLOSED')
      case 'all':
      default:
        return impediments
    }
  }

  const displayedImpediments = filteredImpediments()

  return (
    <>
      {/* Page Header */}
      <Row className='mb-4'>
        <Col>
          <div className='d-flex justify-content-between align-items-center'>
            <div>
              <h2 className='fw-bold mb-1'>Impediment Tracking</h2>
              <p className='text-muted mb-0'>
                Log and track blockers, issues, and impediments
              </p>
            </div>
            <Button variant='primary' onClick={openModal}>
              <i className='fas fa-plus me-2'></i>
              Log Impediment
            </Button>
          </div>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className='mb-4'>
        <Col md={3}>
          <Card className='border-danger'>
            <Card.Body>
              <div className='d-flex align-items-center'>
                <div className='fs-2 text-danger me-3'>
                  <i className='bi bi-exclamation-circle'></i>
                </div>
                <div>
                  <div className='text-muted small'>Open</div>
                  <div className='fs-4 fw-bold'>{summary.OPEN || 0}</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className='border-warning'>
            <Card.Body>
              <div className='d-flex align-items-center'>
                <div className='fs-2 text-warning me-3'>
                  <i className='bi bi-hourglass-split'></i>
                </div>
                <div>
                  <div className='text-muted small'>In Progress</div>
                  <div className='fs-4 fw-bold'>{summary.IN_PROGRESS || 0}</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className='border-success'>
            <Card.Body>
              <div className='d-flex align-items-center'>
                <div className='fs-2 text-success me-3'>
                  <i className='bi bi-check-circle'></i>
                </div>
                <div>
                  <div className='text-muted small'>Resolved</div>
                  <div className='fs-4 fw-bold'>{summary.RESOLVED || 0}</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className='border-info'>
            <Card.Body>
              <div className='d-flex align-items-center'>
                <div className='fs-2 text-info me-3'>
                  <i className='bi bi-list-check'></i>
                </div>
                <div>
                  <div className='text-muted small'>Total</div>
                  <div className='fs-4 fw-bold'>{impediments.length}</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Impediments List with Tabs */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'open')}>
                <Tab
                  eventKey='open'
                  title={
                    <span>
                      Open <Badge bg='danger'>{summary.OPEN || 0}</Badge>
                    </span>
                  }
                />
                <Tab
                  eventKey='in-progress'
                  title={
                    <span>
                      In Progress <Badge bg='warning'>{summary.IN_PROGRESS || 0}</Badge>
                    </span>
                  }
                />
                <Tab
                  eventKey='resolved'
                  title={
                    <span>
                      Resolved <Badge bg='success'>{summary.RESOLVED || 0}</Badge>
                    </span>
                  }
                />
                <Tab eventKey='all' title='All' />
              </Tabs>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className='text-center py-5'>
                  <div className='spinner-border text-primary' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                </div>
              ) : displayedImpediments.length === 0 ? (
                <div className='text-center py-5'>
                  <i className='bi bi-check-circle fs-1 text-success'></i>
                  <p className='text-muted mt-3'>
                    No {activeTab === 'all' ? '' : activeTab.replace('-', ' ')} impediments
                  </p>
                </div>
              ) : (
                <div className='row g-3'>
                  {displayedImpediments.map((impediment) => (
                    <div key={impediment.id} className='col-md-6 col-lg-4'>
                      <Card 
                        className='h-100 cursor-pointer hover-shadow' 
                        onClick={() => openDetailModal(impediment)}
                        style={{cursor: 'pointer'}}
                      >
                        <Card.Body>
                          <div className='d-flex justify-content-between align-items-start mb-2'>
                            <Badge bg={getPriorityColor(impediment.priority)}>
                              {impediment.priority}
                            </Badge>
                            <Badge bg={getStatusColor(impediment.status)}>
                              {impediment.status}
                            </Badge>
                          </div>
                          <h6 className='fw-bold mb-2'>{impediment.title}</h6>
                          <p className='small text-muted mb-2' style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {impediment.description}
                          </p>
                          {impediment.impactedArea && (
                            <div className='small text-primary mb-2'>
                              <i className='bi bi-tag me-1'></i>
                              {impediment.impactedArea}
                            </div>
                          )}
                          <div className='small text-muted'>
                            <i className='bi bi-clock me-1'></i>
                            {format(parseISO(impediment.createdAt), 'MMM dd, yyyy')}
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Log New Impediment</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className='mb-3'>
              <Form.Label>
                Title <span className='text-danger'>*</span>
              </Form.Label>
              <Form.Control
                type='text'
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                isInvalid={!!formErrors.title}
                placeholder='Brief summary of the impediment...'
              />
              <Form.Control.Feedback type='invalid'>{formErrors.title}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>
                Description <span className='text-danger'>*</span>
              </Form.Label>
              <Form.Control
                as='textarea'
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                isInvalid={!!formErrors.description}
                placeholder='Detailed description of the issue or blocker...'
              />
              <Form.Control.Feedback type='invalid'>{formErrors.description}</Form.Control.Feedback>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>
                    Priority <span className='text-danger'>*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({...formData, priority: e.target.value as ImpedimentPriority})
                    }
                  >
                    <option value='LOW'>Low</option>
                    <option value='MEDIUM'>Medium</option>
                    <option value='HIGH'>High</option>
                    <option value='CRITICAL'>Critical</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>Impacted Area</Form.Label>
                  <Form.Control
                    type='text'
                    value={formData.impactedArea}
                    onChange={(e) => setFormData({...formData, impactedArea: e.target.value})}
                    placeholder='e.g., Frontend, Backend, Database'
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant='primary' type='submit'>
              Log Impediment
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Impediment Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedImpediment && (
            <>
              <div className='d-flex justify-content-between align-items-start mb-3'>
                <div>
                  <Badge bg={getPriorityColor(selectedImpediment.priority)} className='me-2'>
                    {selectedImpediment.priority}
                  </Badge>
                  <Badge bg={getStatusColor(selectedImpediment.status)}>
                    {selectedImpediment.status}
                  </Badge>
                </div>
                <Button
                  size='sm'
                  variant='outline-danger'
                  onClick={() => {
                    handleDelete(selectedImpediment.id)
                    setShowDetailModal(false)
                  }}
                >
                  <i className='fas fa-trash'></i>
                </Button>
              </div>

              <h5 className='fw-bold mb-3'>{selectedImpediment.title}</h5>
              <p className='mb-3'>{selectedImpediment.description}</p>

              {selectedImpediment.impactedArea && (
                <div className='mb-3'>
                  <strong>Impacted Area:</strong> {selectedImpediment.impactedArea}
                </div>
              )}

              <div className='mb-3'>
                <strong>Created:</strong>{' '}
                {format(parseISO(selectedImpediment.createdAt), 'MMMM dd, yyyy HH:mm')}
              </div>

              {selectedImpediment.resolvedAt && (
                <div className='mb-3'>
                  <strong>Resolved:</strong>{' '}
                  {format(parseISO(selectedImpediment.resolvedAt), 'MMMM dd, yyyy HH:mm')}
                </div>
              )}

              <hr />

              <h6 className='fw-bold mb-3'>Update Status</h6>
              <div className='d-flex gap-2 flex-wrap mb-3'>
                {selectedImpediment.status !== 'IN_PROGRESS' && (
                  <Button
                    size='sm'
                    variant='warning'
                    onClick={() => handleUpdateStatus(selectedImpediment.id, ImpedimentStatus.IN_PROGRESS)}
                  >
                    Mark In Progress
                  </Button>
                )}
                {selectedImpediment.status !== 'RESOLVED' && (
                  <Button
                    size='sm'
                    variant='success'
                    onClick={() => handleUpdateStatus(selectedImpediment.id, ImpedimentStatus.RESOLVED)}
                  >
                    Mark Resolved
                  </Button>
                )}
                {selectedImpediment.status !== 'CLOSED' && (
                  <Button
                    size='sm'
                    variant='secondary'
                    onClick={() => handleUpdateStatus(selectedImpediment.id, ImpedimentStatus.CLOSED)}
                  >
                    Close
                  </Button>
                )}
              </div>

              <hr />

              <h6 className='fw-bold mb-3'>Comments</h6>
              {selectedImpediment.comments && selectedImpediment.comments.length > 0 ? (
                <div className='mb-3'>
                  {selectedImpediment.comments.map((c) => (
                    <div key={c.id} className='card mb-2'>
                      <div className='card-body p-3'>
                        <div className='d-flex justify-content-between'>
                          <strong className='small'>
                            {c.user.firstName} {c.user.lastName}
                          </strong>
                          <small className='text-muted'>
                            {format(parseISO(c.createdAt), 'MMM dd, HH:mm')}
                          </small>
                        </div>
                        <p className='mb-0 mt-2 small'>{c.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className='text-muted small mb-3'>No comments yet</p>
              )}

              <Form.Group className='mb-0'>
                <Form.Control
                  as='textarea'
                  rows={2}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder='Add a comment...'
                />
                <Button
                  size='sm'
                  variant='primary'
                  className='mt-2'
                  onClick={handleAddComment}
                  disabled={!comment.trim()}
                >
                  Add Comment
                </Button>
              </Form.Group>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default ImpedimentTracking


